import { ChangeDetectionStrategy, Component, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Sidebar } from './components/main/sidebar/sidebar';
import { Topbar } from './components/main/topbar/topbar';
import { TrialBanner } from './components/main/trial-banner/trial-banner';
import { VersionBanner } from './components/main/version-banner/version-banner';
import { Banner } from './components/ui/banner/banner';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, fromEvent, map, merge, of, startWith } from 'rxjs';
import { WINDOW } from './injectors/window';
import { AnalyticsService } from './services/analytics/analytics.service';
import { VersionUpdateService } from './services/version-update/version-update.service';

@Component({
  selector: 'skk-root',
  imports: [RouterOutlet, Sidebar, Topbar, TrialBanner, VersionBanner, Banner],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly win = inject(WINDOW);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly analytics = inject(AnalyticsService);
  private readonly versionUpdate = inject(VersionUpdateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Boot Plausible (no-op outside the browser / in non-prod) and report every
    // route change as a sanitised pageview.
    this.analytics.init();
    // Start watching for new app builds so the version banner can prompt a reload.
    this.versionUpdate.init();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.analytics.trackPageview(event.urlAfterRedirects));
  }

  private readonly routeData$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => {
      let route = this.activatedRoute;
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route.snapshot.data;
    }),
  );

  readonly isOffline = toSignal(
    isPlatformBrowser(this.platformId)
      ? merge(
          of(!this.win.navigator.onLine),
          fromEvent(this.win, 'online').pipe(map(() => false)),
          fromEvent(this.win, 'offline').pipe(map(() => true)),
        )
      : of(false),
    { initialValue: isPlatformBrowser(this.platformId) ? !this.win.navigator.onLine : false },
  );

  public readonly hideSidebar = toSignal<boolean>(
    this.routeData$.pipe(
      map((data) => data['hideSidebar'] ?? false),
      startWith(true),
    ),
  );

  public readonly hideTopbar = toSignal<boolean>(
    this.routeData$.pipe(
      map((data) => data['hideTopbar'] ?? false),
      startWith(true),
    ),
  );
}
